export default function Process() {
  const steps = [
    {
      icon: 'ri-mountain-line',
      title: 'Mountain Harvest',
      description: 'Our olives grow in the pristine mountainous regions of Tunisia, where the unique terroir imparts exceptional flavor and quality to every fruit.',
      image: 'https://public.readdy.ai/ai/img_res/af5fb7d0b5a7f85245e5aa678d115bc8.jpg'
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Hand Selection',
      description: 'Each olive is carefully handpicked by experienced harvesters who can identify the perfect moment of ripeness, ensuring optimal oil quality.',
      image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20hands%20gently%20picking%20ripe%20green%20and%20purple%20olives%20from%20olive%20tree%20branch%20with%20silvery%20leaves%20warm%20natural%20sunlight%20highlighting%20the%20careful%20harvesting%20process%20authentic%20agricultural%20scene%20showing%20traditional%20handpicking%20method%20soft%20focus%20background%20with%20warm%20golden%20and%20green%20tones%20documentary%20style%20photography&width=600&height=400&seq=process-handpick-001&orientation=landscape'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Cold-Pressed Milling',
      description: 'Within hours of harvest, our olives undergo meticulous cold-pressed milling in a controlled, compliant environment that blends modern precision with time-honored techniques.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20clean%20olive%20oil%20production%20facility%20with%20stainless%20steel%20cold-press%20machinery%20processing%20fresh%20green%20olives%20in%20hygienic%20controlled%20environment%20professional%20food%20processing%20setting%20with%20natural%20lighting%20showing%20careful%20meticulous%20milling%20operation%20blend%20of%20contemporary%20equipment%20and%20traditional%20methods%20pristine%20white%20and%20silver%20surfaces%20with%20green%20olive%20tones%20clean%20industrial%20aesthetic%20without%20watermarks%20or%20text&width=600&height=400&seq=process-coldpress-modern-003&orientation=landscape'
    },
    {
      icon: 'ri-drop-line',
      title: 'Pure Extraction',
      description: 'Our cold-press method maintains temperatures below 27°C, ensuring the oil retains its full spectrum of antioxidants, polyphenols, and delicate flavors.',
      image: 'https://readdy.ai/api/search-image?query=Golden%20green%20extra%20virgin%20olive%20oil%20flowing%20and%20pouring%20in%20smooth%20stream%20close%20up%20macro%20photography%20showing%20rich%20texture%20and%20clarity%20of%20premium%20cold-pressed%20oil%20warm%20golden%20and%20green%20hues%20with%20soft%20natural%20lighting%20highlighting%20purity%20and%20quality%20clean%20simple%20background%20emphasizing%20the%20liquid%20gold%20aesthetic&width=600&height=400&seq=process-extract-001&orientation=landscape'
    }
  ];

  return (
    <section id="process" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            From Grove to Bottle
          </span>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our Natural Process
          </h3>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Every step of our production honors centuries of <strong>Mediterranean tradition</strong> while maintaining the highest standards of quality and purity
          </p>
        </div>

        <div className="space-y-20">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-amber-600 text-3xl`}></i>
                  </div>
                  <div>
                    <span className="text-amber-600 text-sm font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Step {index + 1}
                    </span>
                    <h4 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.title}
                    </h4>
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {step.description}
                </p>
              </div>
              
              <div className={`relative h-80 rounded-2xl overflow-hidden shadow-xl ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <img 
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 bg-amber-50 rounded-2xl p-10 border border-amber-100">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-leaf-line text-white text-4xl"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h4 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Cold-Pressed Excellence
              </h4>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Our commitment to <strong>cold-pressing</strong> ensures that every bottle of <strong>Carthaginian Treasure</strong> retains the full nutritional profile nature intended. This gentle process preserves heat-sensitive compounds like <strong>polyphenols</strong> and <strong>antioxidants</strong>, delivering not just exceptional taste, but genuine health benefits in every drop.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
